import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const DataAnalytics = () => {
  return (
    <>
      <SEO 
        title="Data Analytics & Business Intelligence Services"
        description="Transform raw data into actionable business insights with Abacies' enterprise data analytics solutions. Advanced BI platforms, predictive analytics, and custom visualization dashboards."
        keywords="data analytics, business intelligence, BI solutions, data visualization, predictive analytics, big data, data warehousing, analytics consulting"
      />

      {/* Hero Section */}
      <section className="hero-section py-10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full text-primary-red font-semibold mb-4 animate-fade-in">
              Data Analytics & Business Intelligence
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              Transform Data into <span className="text-gradient">Strategic Insights</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Unlock the full potential of your data with enterprise-grade analytics solutions that drive informed decision-making, operational efficiency, and competitive advantage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Request Analytics Consultation
              </Link>
              <a href="#capabilities" className="btn-secondary">
                Explore Capabilities
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-5 bg-white border-b border-platinum">
        <div className="page-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">10PB+</div>
              <div className="text-sm text-titanium">Data Processed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">99.9%</div>
              <div className="text-sm text-titanium">Data Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">40%</div>
              <div className="text-sm text-titanium">Faster Insights</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">500+</div>
              <div className="text-sm text-titanium">Analytics Projects</div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                Enterprise Data Analytics Solutions
              </h2>
              <p className="text-titanium mb-6">
                In today's data-driven business landscape, the ability to extract meaningful insights from vast amounts of information is no longer optional—it's essential. Our comprehensive data analytics services help organizations harness the power of their data to drive strategic decisions, optimize operations, and gain competitive advantages.
              </p>
              <p className="text-titanium mb-6">
                We combine cutting-edge analytics technologies with deep industry expertise to deliver solutions that transform raw data into actionable intelligence. From data warehousing and ETL processes to advanced predictive modeling and real-time dashboards, we provide end-to-end analytics capabilities.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-blush-light/30 text-primary-red rounded-lg text-sm font-medium">
                  Real-Time Analytics
                </span>
                <span className="px-4 py-2 bg-blush-light/30 text-primary-red rounded-lg text-sm font-medium">
                  Predictive Modeling
                </span>
                <span className="px-4 py-2 bg-blush-light/30 text-primary-red rounded-lg text-sm font-medium">
                  Data Visualization
                </span>
                <span className="px-4 py-2 bg-blush-light/30 text-primary-red rounded-lg text-sm font-medium">
                  Big Data Processing
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="card text-center">
                <div className="w-16 h-16 bg-blush-light/40 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-charcoal mb-2">Descriptive Analytics</h3>
                <p className="text-sm text-titanium">Historical data analysis and reporting</p>
              </div>
              <div className="card text-center">
                <div className="w-16 h-16 bg-blush-light/40 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="font-semibold text-charcoal mb-2">Predictive Analytics</h3>
                <p className="text-sm text-titanium">Future trend forecasting and modeling</p>
              </div>
              <div className="card text-center">
                <div className="w-16 h-16 bg-blush-light/40 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-charcoal mb-2">Prescriptive Analytics</h3>
                <p className="text-sm text-titanium">Optimization and recommendation engines</p>
              </div>
              <div className="card text-center">
                <div className="w-16 h-16 bg-blush-light/40 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="font-semibold text-charcoal mb-2">Diagnostic Analytics</h3>
                <p className="text-sm text-titanium">Root cause analysis and insights</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section id="capabilities" className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Comprehensive Analytics Capabilities
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              End-to-end data analytics solutions designed to meet the complex needs of enterprise organizations
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4",
                title: "Data Warehousing & ETL",
                description: "Design and implement scalable data warehouses with automated ETL pipelines for consolidating data from multiple sources."
              },
              {
                icon: "M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z",
                title: "Business Intelligence Platforms",
                description: "Deploy enterprise BI solutions with interactive dashboards, self-service analytics, and comprehensive reporting capabilities."
              },
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Advanced Data Visualization",
                description: "Create stunning, interactive visualizations and custom dashboards that make complex data easily understandable."
              },
              {
                icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                title: "Predictive Analytics & ML",
                description: "Build predictive models using machine learning to forecast trends, identify patterns, and anticipate future outcomes."
              },
              {
                icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Real-Time Analytics",
                description: "Implement streaming analytics solutions for real-time data processing, monitoring, and instant decision-making."
              },
              {
                icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
                title: "Big Data Processing",
                description: "Handle massive datasets with distributed computing frameworks and cloud-based big data technologies."
              },
              {
                icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                title: "Data Quality Management",
                description: "Ensure data accuracy, consistency, and reliability with comprehensive data quality frameworks and governance."
              },
              {
                icon: "M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4",
                title: "Data Integration Services",
                description: "Connect disparate data sources and systems with seamless integration solutions for unified analytics."
              },
              {
                icon: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z",
                title: "Self-Service Analytics",
                description: "Empower business users with intuitive self-service tools for ad-hoc analysis and report generation."
              }
            ].map((capability, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={capability.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{capability.title}</h3>
                <p className="text-titanium">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Best-in-Class Technology Stack
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              We leverage industry-leading tools and platforms to deliver robust analytics solutions
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card text-center">
              <h3 className="font-semibold text-charcoal mb-3">BI Platforms</h3>
              <div className="space-y-2 text-sm text-titanium">
                <p>Power BI</p>
                <p>Tableau</p>
                <p>Looker</p>
                <p>Qlik Sense</p>
              </div>
            </div>
            <div className="card text-center">
              <h3 className="font-semibold text-charcoal mb-3">Big Data</h3>
              <div className="space-y-2 text-sm text-titanium">
                <p>Apache Hadoop</p>
                <p>Apache Spark</p>
                <p>Apache Kafka</p>
                <p>Databricks</p>
              </div>
            </div>
            <div className="card text-center">
              <h3 className="font-semibold text-charcoal mb-3">Data Warehouses</h3>
              <div className="space-y-2 text-sm text-titanium">
                <p>Snowflake</p>
                <p>Amazon Redshift</p>
                <p>Google BigQuery</p>
                <p>Azure Synapse</p>
              </div>
            </div>
            <div className="card text-center">
              <h3 className="font-semibold text-charcoal mb-3">ML & Analytics</h3>
              <div className="space-y-2 text-sm text-titanium">
                <p>Python (Pandas, NumPy)</p>
                <p>R Programming</p>
                <p>TensorFlow</p>
                <p>SciKit-Learn</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Real-World Use Cases
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              See how our data analytics solutions drive business value across industries
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                industry: "Retail & E-commerce",
                title: "Customer Behavior Analytics",
                description: "Analyze shopping patterns, optimize inventory, personalize recommendations, and predict customer churn with advanced analytics.",
                metrics: ["35% increase in conversion", "25% reduction in churn"]
              },
              {
                industry: "Financial Services",
                title: "Risk Analytics & Fraud Detection",
                description: "Real-time fraud detection, credit risk assessment, regulatory compliance reporting, and portfolio optimization.",
                metrics: ["90% fraud detection rate", "40% faster risk assessment"]
              },
              {
                industry: "Healthcare",
                title: "Patient Outcomes Analysis",
                description: "Predictive modeling for patient outcomes, resource optimization, treatment effectiveness analysis, and population health management.",
                metrics: ["20% improved outcomes", "30% cost reduction"]
              },
              {
                industry: "Manufacturing",
                title: "Operational Efficiency Analytics",
                description: "Predictive maintenance, supply chain optimization, quality control analytics, and production forecasting.",
                metrics: ["45% reduction in downtime", "25% cost savings"]
              }
            ].map((useCase, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-primary-red text-white text-sm rounded-full">
                    {useCase.industry}
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{useCase.title}</h3>
                <p className="text-titanium mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.metrics.map((metric, i) => (
                    <span key={i} className="px-3 py-1 bg-blush-light/30 text-primary-red text-sm rounded-lg">
                      {metric}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Our Analytics Implementation Process
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              A proven methodology for delivering successful data analytics projects
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  step: "01",
                  title: "Discovery & Assessment",
                  description: "Understand business objectives, assess data landscape, identify key stakeholders, and define success metrics."
                },
                {
                  step: "02",
                  title: "Data Strategy & Architecture",
                  description: "Design data architecture, establish governance frameworks, select appropriate technologies, and create implementation roadmap."
                },
                {
                  step: "03",
                  title: "Data Integration & Preparation",
                  description: "Build ETL pipelines, integrate data sources, implement data quality checks, and create unified data models."
                },
                {
                  step: "04",
                  title: "Analytics Development",
                  description: "Develop dashboards, create reports, build predictive models, and implement advanced analytics capabilities."
                },
                {
                  step: "05",
                  title: "Testing & Validation",
                  description: "Validate data accuracy, test analytics outputs, conduct user acceptance testing, and ensure performance optimization."
                },
                {
                  step: "06",
                  title: "Deployment & Training",
                  description: "Deploy to production, train end users, establish monitoring systems, and provide comprehensive documentation."
                },
                {
                  step: "07",
                  title: "Optimization & Support",
                  description: "Monitor performance, gather feedback, optimize queries, add new features, and provide ongoing support."
                }
              ].map((phase, index) => (
                <div key={index} className="flex items-start space-x-6 card hover:shadow-lg transition-all duration-300">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-primary-red to-deep-garnet rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    {phase.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2 text-charcoal">{phase.title}</h3>
                    <p className="text-titanium">{phase.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Industries We Serve
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              Delivering specialized analytics solutions across diverse sectors
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Healthcare & Life Sciences", link: "/solutions/healthcare-life-sciences" },
              { name: "Financial Services", link: "/solutions/financial-services" },
              { name: "Retail & E-commerce", link: "/solutions/retail-ecommerce" },
              { name: "Manufacturing & Logistics", link: "/solutions/manufacturing-logistics" },
              { name: "Technology & Software", link: "/solutions/technology-software" },
              { name: "Government & Public Sector", link: "/solutions/government-public-sector" }
            ].map((industry, index) => (
              <Link 
                key={index}
                to={industry.link}
                className="card text-center hover:shadow-xl transition-all duration-300 group"
              >
                <h3 className="font-semibold text-charcoal group-hover:text-primary-red transition-colors">
                  {industry.name}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Why Choose Abacies for Data Analytics
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Deep Technical Expertise",
                description: "Our team of data scientists, analysts, and engineers brings years of experience in implementing enterprise analytics solutions."
              },
              {
                title: "Industry-Specific Solutions",
                description: "Pre-built analytics frameworks and models tailored to specific industry requirements and best practices."
              },
              {
                title: "Scalable Architecture",
                description: "Solutions designed to grow with your business, handling increasing data volumes and complexity seamlessly."
              },
              {
                title: "End-to-End Services",
                description: "From strategy and architecture to implementation and support, we provide comprehensive analytics services."
              },
              {
                title: "Data Security & Compliance",
                description: "Enterprise-grade security measures and compliance with industry regulations (GDPR, HIPAA, SOC 2)."
              },
              {
                title: "Measurable ROI",
                description: "Focus on delivering tangible business value with clear metrics and continuous optimization."
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-charcoal">{benefit.title}</h3>
                <p className="text-titanium text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Data into Business Value?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Let's discuss how our data analytics solutions can help you make better decisions, optimize operations, and drive growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Consultation
            </Link>
            <Link to="/solutions" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              View Industry Solutions
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-5 bg-alabaster">
        <div className="page-container">
          <h2 className="text-2xl font-bold mb-8 text-charcoal text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/services/ai-machine-learning" className="card hover:shadow-xl transition-all duration-300 text-center group">
              <h3 className="font-semibold text-charcoal group-hover:text-primary-red mb-2">AI & Machine Learning</h3>
              <p className="text-sm text-titanium">Advanced AI models and predictive analytics</p>
            </Link>
            <Link to="/services/cloud-implementation" className="card hover:shadow-xl transition-all duration-300 text-center group">
              <h3 className="font-semibold text-charcoal group-hover:text-primary-red mb-2">Cloud Implementation</h3>
              <p className="text-sm text-titanium">Scalable cloud infrastructure for analytics</p>
            </Link>
            <Link to="/services/custom-software" className="card hover:shadow-xl transition-all duration-300 text-center group">
              <h3 className="font-semibold text-charcoal group-hover:text-primary-red mb-2">Custom Software</h3>
              <p className="text-sm text-titanium">Bespoke analytics applications</p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default DataAnalytics;